import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { OrderDetail, OrderStatus } from '@/types';

const STATUS_TONE: Record<OrderStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  successful: 'bg-blue-100 text-blue-700',
  marked_as_packed: 'bg-violet-100 text-violet-700',
  shipped: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-teal-100 text-teal-700',
  settled: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-200 text-slate-700',
  refunded: 'bg-slate-200 text-slate-700',
  failed: 'bg-red-100 text-red-700',
  lost: 'bg-red-100 text-red-700',
};

function formatNaira(numStr: string) {
  const n = Number(numStr);
  if (!Number.isFinite(n)) return numStr;
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
  }).format(n);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function OrderDetailPage() {
  const { reference } = useParams<{ reference: string }>();
  const { hasPermission } = useAuth();
  const canSettle = hasPermission('orders.settle');

  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    if (!reference) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<OrderDetail>(
        `/back-office/orders/${reference}`
      );
      setDetail(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load order'));
    } finally {
      setLoading(false);
    }
  }, [reference]);

  useEffect(() => {
    load();
  }, [load]);

  const settle = async (force: boolean) => {
    if (!detail || !canSettle) return;
    const verb = force ? 'force-settle' : 'settle';
    if (
      !window.confirm(
        `${verb[0].toUpperCase()}${verb.slice(1)} order ${detail.order.reference}? This will release funds to the store.`
      )
    )
      return;
    setActing(true);
    try {
      await api.post(`/back-office/orders/${detail.order.reference}/settle`, {
        force,
      });
      await load();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, 'Failed to settle'));
    } finally {
      setActing(false);
    }
  };

  const advance = async (
    to: 'marked_as_packed' | 'shipped' | 'delivered'
  ) => {
    if (!detail || !canSettle) return;
    const labels: Record<typeof to, string> = {
      marked_as_packed: 'mark as packed',
      shipped: 'mark as shipped',
      delivered: 'mark as delivered',
    };
    if (
      !window.confirm(
        `${labels[to]} order ${detail.order.reference}? This will override the courier system.`
      )
    )
      return;
    setActing(true);
    try {
      await api.post(`/back-office/orders/${detail.order.reference}/advance`, {
        to,
      });
      await load();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, `Failed to ${labels[to]}`));
    } finally {
      setActing(false);
    }
  };

  // Determine the next legal advance target for the current status. Mirrors
  // the payments-ms state machine's fulfilment lane:
  //   successful → marked_as_packed → shipped → delivered
  const nextAdvance: 'marked_as_packed' | 'shipped' | 'delivered' | null =
    detail?.order.status === 'successful'
      ? 'marked_as_packed'
      : detail?.order.status === 'marked_as_packed'
        ? 'shipped'
        : detail?.order.status === 'shipped'
          ? 'delivered'
          : null;

  if (loading) return <div className="px-8 py-6 text-slate-500">Loading…</div>;

  if (error || !detail) {
    return (
      <div className="px-8 py-6">
        <Link to="/orders" className="text-sm text-brand-600 hover:underline">
          ← Back to orders
        </Link>
        <div className="mt-4 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error ?? 'Order not found'}
        </div>
      </div>
    );
  }

  const { order, items, customer, settlement } = detail;
  const itemsTotal = items.reduce(
    (acc, i) => acc + Number(i.total_price || 0),
    0
  );

  return (
    <div className="px-8 py-6">
      <Link to="/orders" className="text-sm text-brand-600 hover:underline">
        ← Back to orders
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-mono text-xl font-semibold text-slate-800">
            {order.reference}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span
              className={clsx(
                'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                STATUS_TONE[order.status]
              )}
            >
              {order.status.replace(/_/g, ' ')}
            </span>
            <span>·</span>
            <span>{formatNaira(order.total_price)}</span>
            <span>·</span>
            <span>
              {order.total_quantity} item{order.total_quantity === 1 ? '' : 's'}
            </span>
            <span>·</span>
            <span>{order.payment_gateway ?? 'unknown gateway'}</span>
          </div>
        </div>
        {canSettle && (
          <div className="flex flex-wrap items-center gap-2">
            {nextAdvance && (
              <button
                type="button"
                onClick={() => advance(nextAdvance)}
                disabled={acting}
                className="rounded border border-brand-300 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50"
              >
                {acting
                  ? '…'
                  : nextAdvance === 'marked_as_packed'
                    ? 'Mark as packed'
                    : nextAdvance === 'shipped'
                      ? 'Mark as shipped'
                      : 'Mark as delivered'}
              </button>
            )}
            {order.status === 'delivered' && !order.settled_at && (
              <>
                {settlement.eligible ? (
                  <button
                    type="button"
                    onClick={() => settle(false)}
                    disabled={acting}
                    className="rounded bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {acting ? '…' : 'Settle order'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => settle(true)}
                    disabled={acting}
                    title="Customer hasn't confirmed and the holdback window hasn't elapsed. This will override the check."
                    className="rounded border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                  >
                    {acting ? '…' : 'Force settle'}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {!settlement.eligible &&
        order.status === 'delivered' &&
        !order.settled_at && (
          <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Not yet eligible for settlement. Either the customer hasn't
            confirmed receipt, or the {settlement.holdback_days}-day holdback
            window hasn't elapsed since delivery.
          </div>
        )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section title="Items">
            {items.length === 0 ? (
              <div className="text-sm text-slate-500">No items recorded.</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-2">Product</th>
                    <th className="py-2 text-right">Qty</th>
                    <th className="py-2 text-right">Unit</th>
                    <th className="py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((i) => (
                    <tr key={i.id}>
                      <td className="py-2">
                        <div className="text-slate-800">
                          {i.product_name ?? i.description ?? `#${i.entity_id}`}
                        </div>
                        {i.entity_type && (
                          <div className="text-xs text-slate-400">
                            {i.entity_type}
                          </div>
                        )}
                      </td>
                      <td className="py-2 text-right text-slate-700">
                        {i.quantity}
                      </td>
                      <td className="py-2 text-right text-slate-700">
                        {formatNaira(i.unit_price)}
                      </td>
                      <td className="py-2 text-right text-slate-800">
                        {formatNaira(i.total_price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      colSpan={3}
                      className="pt-3 text-right text-sm font-medium text-slate-600"
                    >
                      Items subtotal
                    </td>
                    <td className="pt-3 text-right text-sm font-semibold text-slate-900">
                      {formatNaira(String(itemsTotal))}
                    </td>
                  </tr>
                  <tr>
                    <td
                      colSpan={3}
                      className="pt-1 text-right text-sm font-medium text-slate-600"
                    >
                      Order total
                    </td>
                    <td className="pt-1 text-right text-sm font-semibold text-slate-900">
                      {formatNaira(order.total_price)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </Section>

          <Section title="Lifecycle">
            <DetailRow label="Created" value={formatDate(order.created_at)} />
            <DetailRow
              label="Last updated"
              value={formatDate(order.updated_at)}
            />
            <DetailRow
              label="Delivered at"
              value={formatDate(order.delivered_at)}
            />
            <DetailRow
              label="Customer confirmed"
              value={formatDate(order.customer_confirmed_at)}
            />
            <DetailRow
              label="Settled at"
              value={formatDate(order.settled_at)}
            />
            <DetailRow
              label="Gateway reference"
              value={order.gateway_reference ?? '—'}
            />
          </Section>
        </div>

        <div className="lg:col-span-1">
          <Section title="Customer">
            {customer ? (
              <>
                <DetailRow
                  label="Name"
                  value={
                    <Link
                      to={`/users/${customer.id}`}
                      className="text-brand-600 hover:underline"
                    >
                      {customer.first_name} {customer.last_name}
                    </Link>
                  }
                />
                <DetailRow label="Email" value={customer.email} />
                <DetailRow label="Phone" value={customer.phone ?? '—'} />
              </>
            ) : (
              <div className="text-sm text-slate-500">No customer linked.</div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded border border-slate-200 bg-white p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right text-slate-800">{value}</span>
    </div>
  );
}
